import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { api, type LeveKpisView } from '../../lib/client.ts';
import { useSession } from '../../lib/session.tsx';
import { Strip } from './project-leve.tsx';

/**
 * The KPI strip of Leve's organisation page (Remote-Leverage/hriq `org-detail.tsx`), on the CRM
 * project page in its own card under the header, one strip per Leve organisation the project's
 * contacts link (`GET /projects/{id}/leve/kpis`, which computes each figure as Leve's `page.tsx` does).
 * Values are written as Leve writes them: `$<n.toLocaleString()>`, "filled/sold".
 *
 * A header must never show an error card: while loading, when nothing links, or when Leve is not
 * set up or not answering, it draws nothing — except one muted line for an admin, who can fix it.
 */
type Kpis = LeveKpisView['organizations'][number];

const dollars = (v: string) => `$${Number(v).toLocaleString('en-US')}`;

export function strip(o: Kpis) {
  return [
    { label: 'Active contractors', value: String(o.activeContractors) },
    { label: 'Active COR VAs', value: String(o.activeCorVas) },
    { label: 'Seats filled', value: `${o.seatsFilled}/${o.seatsSold}` },
    { label: 'Deposit held', value: dollars(o.depositHeld) },
    { label: 'Client owes RL', value: dollars(o.clientOwes) },
    { label: 'Paid to contractors', value: dollars(o.paidToContractors) },
    { label: 'Pending to contractors', value: dollars(o.pendingToContractors) },
  ];
}

export function LeveKpis({ projectId }: { projectId: string }) {
  const session = useSession();
  const query = useQuery({
    queryKey: ['leve-kpis', projectId],
    queryFn: () => api.leve.kpis(projectId),
    staleTime: 60_000,
  });
  const data = query.data;
  if (!data) return null;
  if (data.status !== 'ready')
    return session.can('organisation.manage') ? (
      <p className="text-muted-foreground text-xs" data-testid="leve-kpis-off">
        Leve figures unavailable: {data.reason}
      </p>
    ) : null;
  if (data.organizations.length === 0) return null;
  return (
    <section
      className="bg-card border-border rounded-card shadow-card space-y-3 border p-5"
      data-testid="leve-kpis"
    >
      {data.organizations.map((o) => (
        <section key={o.id} aria-label={`Leve KPIs: ${o.name}`} className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="text-muted-foreground text-xs font-bold tracking-[0.08em] uppercase">
              Leve · {o.name}
            </p>
            {o.url && (
              <a
                href={o.url}
                target="_blank"
                rel="noreferrer"
                className="text-primary inline-flex items-center gap-1 text-xs hover:underline"
              >
                Open in Leve <ExternalLink className="size-3" aria-hidden />
              </a>
            )}
          </div>
          <Strip items={strip(o)} />
        </section>
      ))}
    </section>
  );
}
